import type { AiConfidence, AiVerdict, IdeaStatus, IdeaTransitionOption } from './types';
import { IDEA_STATUSES } from './types';
import type { Tone } from './format';

/**
 * 创意状态机 —— 唯一事实来源。
 *
 * 前端用它决定渲染哪些按钮，Worker 用它做强制校验（前端只是体验层）。
 * 这里**没有**审批链：每一步都是单向的可选动作，唯一可回退的是「重新验证」与「退回已验证」。
 */
export const IDEA_STATUS_LABELS: Readonly<Record<IdeaStatus, string>> = {
  draft: '草稿',
  submitted: '待验证',
  validating: '验证中',
  validated: '已验证',
  incubating: '立项中',
  shipped: '已上线',
  archived: '已归档',
};

export const IDEA_STATUS_DESCRIPTIONS: Readonly<Record<IdeaStatus, string>> = {
  draft: '尚未提交，只有作者可见。',
  submitted: '已提交，等待发起 AI 研判。',
  validating: 'AI 正在研判，通常几十秒内出结论。',
  validated: '已有 AI 研判结论，等待评审或补充信息。',
  incubating: '已立项，指定了负责人与目标时间。',
  shipped: '已经上线或落地完成。',
  archived: '已归档，不再推进，但讨论与研判结论全部保留。',
};

export const IDEA_STATUS_TONES: Readonly<Record<IdeaStatus, Tone>> = {
  draft: 'neutral',
  submitted: 'info',
  validating: 'accent',
  validated: 'ok',
  incubating: 'caution',
  shipped: 'ok',
  archived: 'neutral',
};

/**
 * 谁可以触发迁移：
 * - `author`：创意的作者本人
 * - `reviewer`：拥有 `idea.transition.review` 权限的人（评审员 / 管理员）
 * - `system`：由服务端在 AI 研判流程中写入，人工不可置位
 */
export type TransitionActor = 'author' | 'reviewer' | 'system';

export interface StatusTransition extends IdeaTransitionOption {
  actor: TransitionActor;
}

export const STATUS_TRANSITIONS: readonly StatusTransition[] = [
  {
    from: 'draft',
    to: 'submitted',
    actor: 'author',
    label: '提交',
    description: '提交后其他人可见，可以发起 AI 研判。',
    requiresAssignee: false,
    tone: 'primary',
  },
  {
    from: 'validated',
    to: 'submitted',
    actor: 'author',
    label: '重新验证',
    description: '在补充信息后重新跑一次研判，历史结论会保留为旧版本。',
    requiresAssignee: false,
    tone: 'default',
  },
  {
    from: 'validated',
    to: 'incubating',
    actor: 'reviewer',
    label: '立项',
    description: '确认推进，需要指定负责人与目标时间。',
    requiresAssignee: true,
    tone: 'primary',
  },
  {
    from: 'incubating',
    to: 'shipped',
    actor: 'reviewer',
    label: '标记上线',
    description: '创意已经落地完成。',
    requiresAssignee: false,
    tone: 'primary',
  },
  {
    from: 'incubating',
    to: 'validated',
    actor: 'reviewer',
    label: '退回已验证',
    description: '暂停推进，回到等待决策的状态。',
    requiresAssignee: false,
    tone: 'default',
  },
  {
    from: 'archived',
    to: 'submitted',
    actor: 'author',
    label: '取回',
    description: '把归档的创意重新放回待验证。',
    requiresAssignee: false,
    tone: 'default',
  },
  // —— 以下由服务端在 AI 研判流程中触发，界面上不出现按钮 ——
  {
    from: 'submitted',
    to: 'validating',
    actor: 'system',
    label: '开始研判',
    description: 'AI 开始研判。',
    requiresAssignee: false,
    tone: 'default',
  },
  {
    from: 'validated',
    to: 'validating',
    actor: 'system',
    label: '开始重新研判',
    description: 'AI 开始重新研判。',
    requiresAssignee: false,
    tone: 'default',
  },
  {
    from: 'validating',
    to: 'validated',
    actor: 'system',
    label: '研判完成',
    description: 'AI 已给出结论。',
    requiresAssignee: false,
    tone: 'default',
  },
  {
    from: 'validating',
    to: 'submitted',
    actor: 'system',
    label: '研判失败',
    description: 'AI 调用失败，已回到待验证，可稍后重试。',
    requiresAssignee: false,
    tone: 'default',
  },
];

/** 归档可以由作者或评审员在任意未归档状态触发，因此单独判定。 */
export const ARCHIVE_SOURCES: readonly IdeaStatus[] = ['draft', 'submitted', 'validating', 'validated', 'incubating', 'shipped'];

export function isIdeaStatus(value: unknown): value is IdeaStatus {
  return typeof value === 'string' && (IDEA_STATUSES as readonly string[]).includes(value);
}

/** 列出某个状态在指定身份下可用的迁移。 */
export function transitionsFrom(status: IdeaStatus, actor: TransitionActor): StatusTransition[] {
  return STATUS_TRANSITIONS.filter((transition) => transition.from === status && transition.actor === actor);
}

/** 列出界面需要渲染成按钮的迁移（排除 system 自动迁移）。 */
export function visibleTransitionsFrom(status: IdeaStatus, actors: readonly TransitionActor[]): StatusTransition[] {
  return STATUS_TRANSITIONS.filter(
    (transition) => transition.from === status && transition.actor !== 'system' && actors.includes(transition.actor),
  );
}

export function findTransition(from: IdeaStatus, to: IdeaStatus, actor: TransitionActor): StatusTransition | null {
  return (
    STATUS_TRANSITIONS.find(
      (transition) => transition.from === from && transition.to === to && transition.actor === actor,
    ) ?? null
  );
}

/**
 * 判定一次迁移是否合法。
 *
 * `authorId` / `actorUserId` 用来确认「作者本人」这一条：拥有 `author` 身份但不拥有该创意的人，一律拒绝。
 */
export function canTransition(params: {
  from: IdeaStatus;
  to: IdeaStatus;
  actor: TransitionActor;
  authorId: string;
  actorUserId: string;
  isReviewer: boolean;
}): StatusTransition | null {
  const { from, to, actor, authorId, actorUserId, isReviewer } = params;
  if (to === 'archived') {
    if (actor === 'system') return null;
    if (!ARCHIVE_SOURCES.includes(from)) return null;
    const isAuthor = authorId === actorUserId;
    if (!isAuthor && !isReviewer) return null;
    return {
      from,
      to,
      actor: isAuthor ? 'author' : 'reviewer',
      label: '归档',
      description: '归档后不再推进，历史内容与研判结论保留。',
      requiresAssignee: false,
      tone: 'danger',
    };
  }
  const transition = findTransition(from, to, actor);
  if (!transition) return null;
  if (actor === 'author' && authorId !== actorUserId) return null;
  if (actor === 'reviewer' && !isReviewer) return null;
  return transition;
}

/**
 * 某个账号在当前状态下可以执行的全部流转。
 *
 * Worker 用它校验请求，前端用它渲染按钮 —— 读的是同一份状态机，
 * 因此不会出现「按钮点得动但服务端拒绝」这种前后端各说各话的情况。
 */
export function availableTransitions(params: {
  status: IdeaStatus;
  authorId: string;
  actorUserId: string;
  isReviewer: boolean;
}): StatusTransition[] {
  const { status, authorId, actorUserId, isReviewer } = params;
  const isAuthor = authorId === actorUserId;
  const actors: TransitionActor[] = [];
  if (isAuthor) actors.push('author');
  if (isReviewer) actors.push('reviewer');
  if (actors.length === 0) return [];

  const collected: StatusTransition[] = [];
  const seen = new Set<string>();

  const push = (transition: StatusTransition | null): void => {
    if (!transition) return;
    const key = `${transition.from}->${transition.to}`;
    if (seen.has(key)) return;
    seen.add(key);
    collected.push(transition);
  };

  for (const actor of actors) {
    for (const candidate of visibleTransitionsFrom(status, [actor])) {
      push(
        canTransition({
          from: candidate.from,
          to: candidate.to,
          actor,
          authorId,
          actorUserId,
          isReviewer,
        }),
      );
    }
  }
  push(canTransition({ from: status, to: 'archived', actor: actors[0] as TransitionActor, authorId, actorUserId, isReviewer }));

  return collected;
}

export const AI_VERDICT_LABELS: Readonly<Record<AiVerdict, string>> = {
  go: '建议推进',
  needs_more_info: '信息不足，建议补充',
  no_go: '不建议推进',
};

export const AI_VERDICT_TONES: Readonly<Record<AiVerdict, Tone>> = {
  go: 'ok',
  needs_more_info: 'caution',
  no_go: 'critical',
};

export const AI_VERDICT_DESCRIPTIONS: Readonly<Record<AiVerdict, string>> = {
  go: '按现有信息判断值得投入一轮最小验证。',
  needs_more_info: '缺少关键信息，补齐后重新研判比直接投入更划算。',
  no_go: '按现有信息判断收益不足以覆盖成本，建议放弃或换方向。',
};

export const AI_CONFIDENCE_LABELS: Readonly<Record<AiConfidence, string>> = {
  high: '把握较高',
  medium: '把握一般',
  low: '把握较低',
};

/**
 * 综合分的档位。刻意不叫「通过 / 不通过」—— 分数只描述信息完备度下的可行性，
 * 决定权仍然在人手里。
 */
export function aiScoreBand(score: number): { key: 'strong' | 'promising' | 'uncertain' | 'weak'; label: string; tone: Tone } {
  if (score >= 75) return { key: 'strong', label: '可行性好', tone: 'ok' };
  if (score >= 55) return { key: 'promising', label: '值得一试', tone: 'info' };
  if (score >= 35) return { key: 'uncertain', label: '需要更多依据', tone: 'caution' };
  return { key: 'weak', label: '可行性偏弱', tone: 'critical' };
}

/** 研判过程中的三态文案，前端状态条直接使用。 */
export const AI_RUN_STATUS_LABELS: Readonly<Record<string, string>> = {
  queued: '排队中',
  running: '研判中',
  succeeded: '已完成',
  failed: '失败',
};

/** 研判完成后不再接受人工编辑，避免出现「看起来是 AI 说的、其实是人编的」结论。 */
export const AI_REPORT_READONLY_NOTE = '研判结论与产物由模型生成，不可人工编辑；不同意见请写进讨论区。';
